import React, {
  useContext,
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
} from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import {
  useStripe,
  useElements,
  CardExpiryElement,
  CardNumberElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import CardNumberIcon from '../../../assets/Card 1.svg';
import CardExpiryIcon from '../../../assets/Vector.svg';
import CardCVVIcon from '../../../assets/Lock 1.svg';
import './Payment.css';
import { Typography } from '@mui/material';
import { Box, Container } from '@mui/material';
import StyledButton from '../../../components/Button/Button';
import Popup from '../../../components/Inputs/Popup';
import { useNavigate } from 'react-router-dom';
import { createSubscription } from '../../../services/NetworkServices';
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import TopNav from '../../../components/TopNav/TopNav';
import { ProfileContext } from '../../../context/ProfileContext';
import ApiStatus from '../../../config/apiStatusConstants';
import CircularProgress from '@mui/material/CircularProgress';
import CardHolderNameInput from '../../../components/Inputs/CardHolderNameInput';
import { AuthContext } from '../../../context/AuthContext';
import { getStage } from '../../../services/NetworkServices';
import { useLocation } from 'react-router-dom';
import { storeStageNumber } from '../../../utils/auth-token-utils/cookie';
import { decreaseStage } from '../../../services/NetworkServices';
import { PaymentMethod } from '@stripe/stripe-js';

interface FloatingLabelInputProps {
  label: string;
  children: React.ReactNode;
  focused: boolean;
  hasValue: boolean;
  hasInput: boolean;
}

export const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  children,
  focused,
  hasValue,
  hasInput,
}) => {
  return (
    <Box
      className={`floating-label-input ${
        focused || hasValue || hasInput ? 'focused' : ''
      }`}
      sx={{ width: '100%', marginLeft: '0px' }}
    >
      <label className="floating-label">{label}</label>
      {children}
    </Box>
  );
};

export const formatName = (input: string) => {
  const formattedName = input.replace(/[^a-zA-Z\s]/g, '').slice(0, 40);
  return formattedName;
};

const Payment: React.FC = () => {
  const profileContext = useContext(ProfileContext);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const autoRenewalString = searchParams.get('autoRenewal');

  const autoRenewal = autoRenewalString === 'true';

  if (!profileContext) {
    throw new Error('ProfileContext must be used within a ProfileProvider');
  }
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const stripe = useStripe();
  const elements = useElements();
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [paymentMethodDetails, setPaymentMethodDetails] =
    useState<PaymentMethod | null>(null);

  const [cardHolderName, setCardHolderName] = useState<string>('');
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [cardNumberComplete, setCardNumberComplete] = useState<boolean>(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState<boolean>(false);
  const [cardCvcComplete, setCardCvcComplete] = useState<boolean>(false);

  const [cardNumberFocused, setCardNumberFocused] = useState<boolean>(false);
  const [cardExpiryFocused, setCardExpiryFocused] = useState<boolean>(false);
  const [cardCvcFocused, setCardCvcFocused] = useState<boolean>(false);

  const [cardNumberHasInput, setCardNumberHasInput] = useState<boolean>(false);
  const [cardExpiryHasInput, setCardExpiryHasInput] = useState<boolean>(false);
  const [cardCvcHasInput, setCardCvcHasInput] = useState<boolean>(false);

  const navigate = useNavigate();

  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error('Auth Context must be used within a AuthProvider');
  }

  useEffect(() => {
    const fetchStageDetails = async () => {
      try {
        console.log('inside use effect');
        const response = await getStage();
        const data = await response.data;
        if (data) {
          console.log('stage is', data.data.stage);
          const stage = data.data.stage;
          storeStageNumber(stage);
          if (stage === '1') {
            navigate('/participant/createProfile');
          }
          if (stage === '2') {
            navigate('/participant/inviteBabySitter');
          }
          if (stage === '4') {
            navigate('/participant/dashboard');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  const handleContinue = () => {
    navigate('/participant/dashboard');
  };

  const onChangeName = (event: ChangeEvent<HTMLInputElement>) => {
    setCardHolderName(event.target.value);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const response = await getStage();
    const data = await response.data;
    if (data) {
      console.log('stage is', data.data.stage);
      const stage = data.data.stage;
      if (stage === '1') {
        navigate('/participant/createprofile');
      } else if (stage === '2') {
        navigate('/participant/inviteBabySitter');
      } else if (stage === '4') {
        navigate('/participant/dashboard');
      } else {
        if (!stripe || !elements) {
          return; // Stripe.js hasn't loaded yet
        }

        const cardElement = elements.getElement(CardNumberElement);

        if (!cardElement) {
          return;
        }

        const { error: paymentError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
              name: cardHolderName,
            },
          });

        if (paymentMethod) {
          setPaymentMethodDetails(paymentMethod);
        }

        if (paymentError) {
          setErrorMsg(paymentError.message || 'An error occurred');
          return;
        }

        const paymentMethodId = paymentMethod?.id;
        try {
          setApiStatus(ApiStatus.LOADING);
          const response = await createSubscription({
            paymentMethod: paymentMethodId,
            autoRenewal,
          });

          const data = await response.data;
          const { clientSecret } = data.data;
          const subscriptionId = data.data.subscription.id;

          const { paymentIntent, error: confirmError } =
            await stripe.confirmCardPayment(clientSecret);

          console.log('payment status is', paymentIntent?.status);

          if (confirmError) {
            setErrorMsg(confirmError.message || 'An error occurred');
            const response = await decreaseStage({ subscriptionId });
            console.log('response is', response);
            setApiStatus(ApiStatus.FAILURE);
          } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            setApiStatus(ApiStatus.SUCCESS);
            setShowPopup(true);
          }
        } catch (err: any) {
          setApiStatus(ApiStatus.FAILURE);
          console.log('error is', err);
          setErrorMsg(
            err.response?.data?.error?.raw?.message || 'An error occurred',
          );
        }
      }
    }
  };

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <StyledButton sx={{ mb: '5vh' }}>
          <CircularProgress size={20} />
        </StyledButton>
      );
    } else {
      return (
        <StyledButton
          disabled={
            !cardNumberComplete ||
            !cardExpiryComplete ||
            !cardCvcComplete ||
            !cardHolderName
          }
          type="submit"
          sx={{ mb: '5vh' }}
        >
          Pay Now
        </StyledButton>
      );
    }
  };

  const renderErrorMsg = () => {
    console.log('error is', errorMsg);
    if (apiStatus === ApiStatus.FAILURE) {
      return <p style={{ color: '#FF1515' }}>{errorMsg}</p>;
    }
    return null;
  };

  return (
    <>
      {showPopup && <div className="backdrop"></div>} {/* Overlay backdrop */}
      <StyledBgContainer className={showPopup ? 'popup-active' : ''}>
        <TopNav showArrow={true} />
        <Container maxWidth="sm">
          <Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',

                px: { sm: 6 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontFamily: 'Opensans',
                  fontWeight: '700',
                }}
              >
                Pay Activation and First Month Fees
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey', mt: 1, mb: 3 }}>
                Associated fees will be charged to the
                <br /> credit card on file.
              </Typography>
              <Box className="plan">
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ fontFamily: 'Opensans' }}>
                    Activation Fee
                  </Typography>
                  <Typography
                    variant="body1"
                    color={'#FFFFFC'}
                    sx={{ fontFamily: 'Opensans' }}
                  >
                    $ 102.17
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ fontFamily: 'Opensans' }}>
                    Monthly Fee
                  </Typography>
                  <Typography
                    variant="body1"
                    color={'white'}
                    sx={{ fontFamily: 'Opensans' }}
                  >
                    $ 194.78
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontFamily: 'Opensans', textAlign: 'left' }}
                  >
                    Credit Card Surcharge
                  </Typography>
                  <Typography variant="body1" color={'#FFFFFC'}>
                    $ 1.00
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    color: 'white',
                    marginTop: '8px',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontFamily: 'Opensans', fontWeight: '700' }}
                  >
                    TOTAL
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontFamily: 'Opensans', fontWeight: '700' }}
                  >
                    $ 297.95
                  </Typography>
                </Box>
              </Box>
              <form className="form" onSubmit={handleSubmit}>
                <CardHolderNameInput
                  type="text"
                  onChange={onChangeName}
                  value={formatName(cardHolderName)}
                  label="Card Holder’s Name"
                />

                <FloatingLabelInput
                  label="Card Number"
                  focused={cardNumberFocused}
                  hasValue={cardNumberComplete}
                  hasInput={cardNumberHasInput}
                >
                  <Box className="card">
                    <InputAdornment position="start">
                      <img
                        src={CardNumberIcon}
                        alt="Card Icon"
                        className="card-icon"
                      />
                    </InputAdornment>
                    <CardNumberElement
                      options={{
                        placeholder: '',
                        style: {
                          base: {
                            color: 'white',
                            fontSize: '16px',
                          },
                        },
                      }}
                      className="card-number"
                      onFocus={() => setCardNumberFocused(true)}
                      onBlur={() => setCardNumberFocused(false)}
                      onChange={(event) => {
                        setCardNumberComplete(event.complete);
                        setCardNumberHasInput(!event.empty);
                      }}
                    />
                  </Box>
                </FloatingLabelInput>

                <Box
                  sx={{
                    display: 'flex',
                    gap: '15px',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <FloatingLabelInput
                    label="Expiry"
                    focused={cardExpiryFocused}
                    hasValue={cardExpiryComplete}
                    hasInput={cardExpiryHasInput}
                  >
                    <Box className="card1">
                      <InputAdornment position="start">
                        <img
                          src={CardExpiryIcon}
                          alt="Expiry Icon"
                          className="expiry-icon"
                        />
                      </InputAdornment>
                      <CardExpiryElement
                        options={{
                          placeholder: '',
                          style: {
                            base: {
                              color: 'white',
                              fontSize: '16px',
                            },
                          },
                        }}
                        className="card-number"
                        onFocus={() => setCardExpiryFocused(true)}
                        onBlur={() => setCardExpiryFocused(false)}
                        onChange={(event) => {
                          setCardExpiryComplete(event.complete);
                          setCardExpiryHasInput(!event.empty);
                        }}
                      />
                    </Box>
                  </FloatingLabelInput>
                  <FloatingLabelInput
                    label="CVV"
                    focused={cardCvcFocused}
                    hasValue={cardCvcComplete}
                    hasInput={cardCvcHasInput}
                  >
                    <Box className="card1">
                      <InputAdornment position="start">
                        <img
                          src={CardCVVIcon}
                          alt="CVV Icon"
                          className="cvv-icon"
                          style={{
                            color: 'white',
                            width: '24px',
                            height: '24px',
                            transform: 'translateY(-9px)',
                          }}
                        />
                      </InputAdornment>
                      <CardCvcElement
                        options={{
                          placeholder: '',
                          style: {
                            base: {
                              color: 'white',
                              fontSize: '16px',
                            },
                          },
                        }}
                        className="card-number"
                        onFocus={() => setCardCvcFocused(true)}
                        onBlur={() => setCardCvcFocused(false)}
                        onChange={(event) => {
                          setCardCvcComplete(event.complete);
                          setCardCvcHasInput(!event.empty);
                        }}
                      />
                    </Box>
                  </FloatingLabelInput>
                </Box>
                {renderErrorMsg()}
                {renderButton()}
              </form>
              {showPopup && (
                <Popup
                  title=<Typography
                    variant="h6"
                    sx={{
                      fontFamily: 'opensans',
                      fontWeight: '700',
                      lineHeight: '1.4',
                    }}
                  >
                    {' '}
                    Payment Completed <br />
                    Successfully{' '}
                  </Typography>
                  message=<Typography variant="body2">
                    {' '}
                    This payment will appear on your credit card statement from{' '}
                    <br /> xxxx xxxx xxxx{' '}
                    {paymentMethodDetails?.card?.last4 || ''}.
                  </Typography>
                  buttonText="Done"
                  onButtonClick={handleContinue}
                />
              )}
            </Box>
          </Box>
        </Container>
      </StyledBgContainer>
    </>
  );
};

export default Payment;
